import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, tokens } from '../lib/api';
import { useApp } from '../lib/app-context';
import { clockTime, relative } from '../lib/format';
import { Avatar, Badge, EmptyState, Spinner } from '../components/ui';
import type { Conversation, Message } from '../lib/types';
import { t } from '../lib/i18n';

/**
 * Messaging.
 *
 * The WebSocket is an accelerator, not the transport: sending always goes through
 * the REST endpoint, and the socket only delivers what other people send. If the
 * socket never connects — some ISP proxies here drop it — a 15-second poll keeps
 * the thread correct, and nothing else in the page changes.
 */
export default function Messages() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const { user, reportError } = useApp();
  const [conversations, setConversations] = useState<Conversation[] | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [live, setLive] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const socketRef = useRef<WebSocket | null>(null);

  const loadConversations = useCallback(async () => {
    try {
      const response = await api.get<{ conversations: Conversation[] }>('/conversations');
      setConversations(response.conversations);
      return response.conversations;
    } catch {
      setConversations([]);
      return [];
    }
  }, []);

  useEffect(() => {
    void (async () => {
      const list = await loadConversations();
      // Land on the most recent thread rather than an empty pane.
      if (!conversationId && list.length > 0) {
        navigate(`/messages/${list[0]?.id}`, { replace: true });
      }
    })();
  }, [conversationId, loadConversations, navigate]);

  const loadMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const response = await api.get<{ messages: Message[] }>(
        `/conversations/${conversationId}/messages?limit=100`,
      );
      setMessages(response.messages);
      await api.post(`/conversations/${conversationId}/read`);
      setConversations((current) =>
        current?.map((item) => (item.id === conversationId ? { ...item, unreadCount: 0 } : item)) ?? null,
      );
    } catch (error) {
      reportError(error, t("Conversation introuvable"));
    }
  }, [conversationId, reportError]);

  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  // Live delivery.
  useEffect(() => {
    const token = tokens.access();
    if (!token) return;
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const socket = new WebSocket(
      `${protocol}://${window.location.host}/v1/ws?token=${encodeURIComponent(token)}`,
    );
    socketRef.current = socket;

    socket.onopen = () => setLive(true);
    socket.onclose = () => setLive(false);
    socket.onerror = () => setLive(false);
    socket.onmessage = (event) => {
      const frame = JSON.parse(String(event.data)) as {
        type: string;
        message?: Message & { conversationId: string };
      };
      if (frame.type !== 'message' || !frame.message) return;
      if (frame.message.conversationId === conversationId) {
        setMessages((current) =>
          current.some((item) => item.id === frame.message?.id) ? current : [...current, frame.message!],
        );
      }
      void loadConversations();
    };

    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, [conversationId, loadConversations]);

  // REST fallback: only polls when the socket is not up.
  useEffect(() => {
    if (live || !conversationId) return;
    const timer = setInterval(() => void loadMessages(), 15_000);
    return () => clearInterval(timer);
  }, [live, conversationId, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const send = async () => {
    if (!conversationId || draft.trim().length === 0) return;
    setSending(true);
    const body = draft.trim();
    setDraft('');
    try {
      const response = await api.post<{ message: Message }>(
        `/conversations/${conversationId}/messages`,
        { body },
      );
      setMessages((current) =>
        current.some((item) => item.id === response.message.id)
          ? current
          : [...current, response.message],
      );
      void loadConversations();
    } catch (error) {
      setDraft(body);
      reportError(error);
    } finally {
      setSending(false);
    }
  };

  if (conversations === null) {
    return (
      <div className="page center" style={{ paddingTop: 60 }}>
        <Spinner label={t("Chargement de la messagerie…")} />
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="page">
        <EmptyState
          title={t("Aucune conversation")}
          description={t("Écrivez à un hôte depuis une annonce, ou répondez à une demande de colocation.")}
        />
      </div>
    );
  }

  const current = conversations.find((item) => item.id === conversationId) ?? null;

  return (
    <div className="page stack">
      <div className="row row--between">
        <h1 style={{ fontSize: 24 }}>{t("Messages")}</h1>
        <span className="tiny muted">
          {live ? t("Temps réel actif") : t("Mode économe — actualisation toutes les 15 s")}
        </span>
      </div>

      <div className="thread">
        <div className="thread__list">
          {conversations.map((conversation) => (
            <button
              key={conversation.id}
              type="button"
              className={`thread__item ${conversation.id === conversationId ? 'thread__item--on' : ''}`}
              onClick={() => navigate(`/messages/${conversation.id}`)}
            >
              <div className="row" style={{ gap: 10 }}>
                <Avatar
                  name={conversation.other?.fullName ?? '?'}
                  url={conversation.other?.avatarUrl}
                  size={36}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="row row--between">
                    <span className="strong small nowrap" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {conversation.other?.fullName ?? t("Utilisateur supprimé")}
                    </span>
                    {conversation.unreadCount > 0 ? (
                      <Badge tone="badge--verified">{conversation.unreadCount}</Badge>
                    ) : null}
                  </div>
                  <div
                    className="tiny muted"
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {conversation.lastMessagePreview || t("Nouvelle conversation")}
                  </div>
                  <div className="tiny muted">
                    {conversation.property?.title ?? conversation.colocationRequest?.city ?? ''} ·{' '}
                    {relative(conversation.lastMessageAt)}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {current ? (
            <div
              className="row row--between"
              style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)', gap: 10 }}
            >
              <div className="row" style={{ gap: 10 }}>
                <Avatar name={current.other?.fullName ?? '?'} url={current.other?.avatarUrl} size={34} />
                <div className="stack stack--tight">
                  <span className="strong small">{current.other?.fullName}</span>
                  {current.other?.identityVerified ? (
                    <span className="tiny" style={{ color: 'var(--teal)' }}>
                      {t("CIN vérifiée")}
                    </span>
                  ) : null}
                </div>
              </div>
              {current.property ? (
                <a className="small" href={`/logement/${current.property.id}`}>
                  {current.property.title}
                </a>
              ) : null}
            </div>
          ) : null}

          <div className="thread__messages">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`bubble ${message.senderId === user?.id ? 'bubble--mine' : ''}`}
              >
                {message.body}
                <span className="bubble__time">{clockTime(message.createdAt)}</span>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div
            className="row"
            style={{ padding: 12, borderTop: '1px solid var(--line)', gap: 8 }}
          >
            <input
              className="input"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void send();
                }
              }}
              placeholder={t("Écrivez votre message…")}
            />
            <button className="btn" disabled={sending || draft.trim().length === 0} onClick={send}>
              {t("Envoyer")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
